import React from 'react';
import * as UI from '../components';

const common = UI.commonStyles || {
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
};

const Card =
  UI.Card ||
  function FallbackCard({ children, title, icon }) {
    return (
      <div style={{ border: '1px solid #eee', padding: '1rem', marginBottom: '1rem', background: '#fff' }}>
        {(title || icon) && (
          <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
            {icon} {title}
          </div>
        )}
        {children}
      </div>
    );
  };

const DataTable =
  UI.DataTable ||
  function FallbackTable({ columns, data }) {
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ textAlign: c.align || 'left', borderBottom: '1px solid #ddd', padding: '0.25rem 0.5rem' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data || []).map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align || 'left', padding: '0.25rem 0.5rem', borderBottom: '1px solid #f3f3f3' }}>
                  {c.render ? c.render(row[c.key], row) : row[c.key] ?? '-'}
                </td>
              ))}
            </tr>
          ))}
          {(!data || data.length === 0) && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: '0.5rem' }}>
                No data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

const SectionHeader =
  UI.SectionHeader ||
  function FallbackHeader({ icon, title, subtitle, action }) {
    return (
      <div style={{ marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '1rem', fontWeight: 600 }}>
            {icon} {title}
          </div>
          {subtitle && <div style={{ fontSize: '0.75rem', color: '#666' }}>{subtitle}</div>}
        </div>
        {action}
      </div>
    );
  };

const LoadingState =
  UI.LoadingState ||
  function FallbackLoading({ message }) {
    return <div style={{ padding: '1rem' }}>{message || 'Loading...'}</div>;
  };

const EmptyState =
  UI.EmptyState ||
  function FallbackEmpty({ title, description }) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#777' }}>
        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{title}</div>
        {description && <div style={{ fontSize: '0.75rem' }}>{description}</div>}
      </div>
    );
  };

const Badge =
  UI.Badge ||
  function FallbackBadge({ label }) {
    return (
      <span style={{ background: '#ffc107', padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.6rem' }}>
        {label}
      </span>
    );
  };

const StatsGrid =
  UI.StatsGrid ||
  function FallbackStatsGrid({ stats }) {
    return (
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {stats.map((s, i) => (
          <div key={i} style={{ border: '1px solid #eee', padding: '0.75rem 1rem', borderRadius: '6px', minWidth: '150px' }}>
            <div style={{ fontSize: '1.5rem' }}>{s.icon}</div>
            <div style={{ fontSize: '0.75rem', color: '#666' }}>{s.label}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{s.value}</div>
          </div>
        ))}
      </div>
    );
  };

const UserRebatesTab = ({
  loading,
  userCode,
  pendingCount,
  rebateTotals,
  userRebates = [],
}) => {
  if (loading) {
    return <LoadingState message="Loading user rebates..." />;
  }

  if (!userCode) {
    return (
      <div style={{ padding: common.spacing.lg }}>
        <EmptyState
          icon="💰"
          title="No User Code"
          description="Enter a user code in the controls above and click 'Get Rebates'"
        />
      </div>
    );
  }

  const stats = [
    {
      icon: '💰',
      label: 'Total Rebates',
      value: `£${rebateTotals?.total_rebate?.toFixed(2) || '0.00'}`,
      color: 'primary',
    },
    {
      icon: '✅',
      label: 'Trust Rebates',
      value: `£${rebateTotals?.trust_rebate?.toFixed(2) || '0.00'}`,
      color: 'success',
    },
    {
      icon: '🤝',
      label: 'Referral Rebates',
      value: `£${rebateTotals?.referral_rebate?.toFixed(2) || '0.00'}`,
      color: 'info',
    },
  ];

  const columns = [
    { key: 'id', label: 'ID', width: '100px' },
    { key: 'sold_date', label: 'Sold Date' },
    { key: 'event', label: 'Event' },
    {
      key: 'trust_rebate',
      label: 'Trust Rebate',
      align: 'right',
      render: (val) => (val ? `£${val.toFixed(2)}` : '£0.00'),
    },
    {
      key: 'referral_rebate',
      label: 'Referral Rebate',
      align: 'right',
      render: (val) => (val ? `£${val.toFixed(2)}` : '£0.00'),
    },
    {
      key: 'total_rebate',
      label: 'Total Rebate',
      align: 'right',
      render: (val) => (val ? `£${val.toFixed(2)}` : '£0.00'),
    },
    {
      key: 'number_of_tickets_sold',
      label: 'Tickets',
      align: 'center',
    },
  ];

  return (
    <div style={{ padding: common.spacing.lg }}>
      <SectionHeader
        icon="💰"
        title="User Rebates"
        subtitle={`User Code: ${userCode}`}
        action={
          pendingCount > 0 && <Badge label={`${pendingCount} Pending`} />
        }
      />

      <StatsGrid stats={stats} columns={3} />

      <Card title="📋 Rebate Details" icon="📊">
        {!userRebates || userRebates.length === 0 ? (
          <EmptyState
            icon="📭"
            title="No Rebates"
            description="No rebate data found for this user"
          />
        ) : (
          <DataTable columns={columns} data={userRebates} empty="No rebate data available" />
        )}
      </Card>

      {userRebates && userRebates.length > 0 && (
        <Card title="ℹ️ Additional Information" icon="ℹ️">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: common.spacing.md }}>
            <div>
              <strong>Total Records:</strong> {userRebates.length}
            </div>
            <div>
              <strong>Total Tickets Sold:</strong>{' '}
              {userRebates.reduce((sum, r) => sum + (r.number_of_tickets_sold || 0), 0)}
            </div>
            <div>
              <strong>Date Range:</strong>{' '}
              {userRebates.length > 0
                ? `${userRebates[0].purchase_date} to ${userRebates[userRebates.length - 1].purchase_date}`
                : 'N/A'}
            </div>
            <div>
              <strong>Pending:</strong> {pendingCount} rebates
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UserRebatesTab;
